import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router';
import createUtmParameters from 'components/Dashboard/helpers/createUtmParameters';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { store } from 'components/Store';
import React, { useContext } from 'react';
import getAppSite from 'utils/getAppSite';
import returnCorrectWPUrl from 'components/Dashboard/utilities/returnCorrectWPUrl';
import ConfigData from 'config/ConfigData';

const PaymentButton = ({
  plan,
  compare,
  activeTab,
  selectedPlanInformation,
  setPlanToCheckout,
  showPopUp,
  bannerConditions,
  conditionForUpgrade,
  className = '',
  trialText,
  link,
  authenticatedButtonText,
}) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activeSubscriptions, team } = state;
  const { myself } = team ?? {};

  const { period } = selectedPlanInformation || {};

  const { planName: mainPlanName, subscriptionStatus } = activeSubscriptions;
  const planName = mainPlanName === 'Campaigner' ? 'Starter' : mainPlanName;

  const plans = ConfigData.plans;
  const positionActivePlan = plans.indexOf(planName);
  const position = plans.indexOf(plan);
  const role = myself !== undefined ? myself.role : '';

  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  const conditionalBlock =
    (subscriptionStatus === 'Subscribed' ||
      subscriptionStatus === 'OnFreePlan' ||
      position < positionActivePlan ||
      role !== 'Owner') &&
    plan === planName;
  const actionConditional = role === 'Owner';

  const blockConditional =
    (plan === planName &&
      (subscriptionStatus === 'InTrial' ||
        subscriptionStatus === 'TrialEnded' ||
        subscriptionStatus === 'SubscriptionEnded') &&
      role !== 'Owner') ||
    (role !== 'Owner' && plan !== planName);

  const utmPlan = period === 'annually' ? 'annual' : 'monthly';

  const utmSourceCode = () => {
    switch (activeTab) {
      case 'agencies':
        return '-agency';
      case 'membership':
        return '-membership-organisation';
      default:
        return '';
    }
  };

  const navigateWithoutReload = (url) => {
    try {
      const parsedUrl = new URL(url, window.location.origin);

      if (parsedUrl.origin === window.location.origin) {
        navigate(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);
        return;
      }
    } catch (error) {
      // Fallback to regular navigation for malformed URLs.
    }

    window.location.assign(url);
  };

  return (
    <>
      {!isAuthenticated && (
        <button
          className={`general-button rounded-pill payment-button px-3 py-2 ${className}`}
          onClick={() => {
            navigateWithoutReload(
              link ??
                `/register?reg_source=pricing-${compare ? 'compare' : utmPlan}-${normalize(
                  plan
                )}${utmSourceCode()}${createUtmParameters('&')}`
            );
          }}
        >
          {trialText || 'Free trial'}
        </button>
      )}
      {isAuthenticated && (
        <button
          className={`general-button rounded-pill payment-button px-3 py-2 ${
            conditionalBlock ? 'payment-button-block' : ''
          } ${className}`}
          data-tooltip-place={'top'}
          data-tooltip-html={blockConditional ? 'Contact your account owner' : ''}
          data-tooltip-id={'pricing-page-tooltip'}
          disabled={blockConditional}
          onClick={async () => {
            if (actionConditional && !bannerConditions && !conditionForUpgrade) {
              setPlanToCheckout(plan);
              showPopUp();
            }
          }}
          style={{
            cursor: actionConditional && !bannerConditions && !conditionForUpgrade ? 'pointer' : 'auto',
          }}
        >
          <>
            {authenticatedButtonText ? (
              <>
                {(subscriptionStatus === 'Subscribed' ||
                  subscriptionStatus === 'OnFreePlan' ||
                  subscriptionStatus === 'OnFreePlanOverLimit' ||
                  subscriptionStatus === 'SubscribedOverLimit') &&
                plan === planName &&
                !conditionForUpgrade
                  ? 'Your plan'
                  : authenticatedButtonText}
              </>
            ) : (
              <>
                {(subscriptionStatus === 'Subscribed' ||
                  subscriptionStatus === 'OnFreePlan' ||
                  subscriptionStatus === 'OnFreePlanOverLimit' ||
                  subscriptionStatus === 'SubscribedOverLimit') &&
                  plan === planName &&
                  !conditionForUpgrade &&
                  `Your plan`}

                {(position > positionActivePlan || (plan === planName && conditionForUpgrade)) && 'Upgrade'}

                {position < positionActivePlan && (subscriptionStatus === 'InTrial' ? 'Subscribe' : 'Downgrade')}
                {subscriptionStatus === 'InTrial' && plan === planName && 'Subscribe'}
                {(subscriptionStatus === 'TrialEnded' || subscriptionStatus === 'SubscriptionEnded') &&
                  plan === planName &&
                  'Reactivate'}
              </>
            )}
            {(blockConditional || bannerConditions || (plan === planName && conditionForUpgrade)) && (
              <>
                <span className='pricing-padlock icon-padlock paragraph-p2 specific-left-margin-8 ' />
              </>
            )}
          </>
        </button>
      )}
    </>
  );
};

const FreeButtons = ({ paymentButtonProps }) => {
  return (
    <>
      <div className='pricing-description px-4'>
        <p className='text-center'>1 user</p>
      </div>
      <div className='p-3'>
        <PaymentButton plan='Free' {...paymentButtonProps} />
      </div>
    </>
  );
};

const BasicButtons = ({ paymentButtonProps }) => {
  return (
    <>
      <div className='pricing-description px-4'>
        <p className='text-center'>1 user</p>
      </div>
      <div className='p-3'>
        <PaymentButton plan='Starter' {...paymentButtonProps} />
      </div>
    </>
  );
};

const ProButtons = ({ paymentButtonProps }) => {
  return (
    <>
      <div className='pricing-description px-4'>
        <p className='text-center'>Unlimited users</p>
      </div>
      <div className='p-3'>
        <PaymentButton plan='Pro' {...paymentButtonProps} />
      </div>
    </>
  );
};

const MogulButtons = ({ paymentButtonProps }) => {
  const { activeTab } = paymentButtonProps || {};
  return (
    <>
      {activeTab === 'companies' && (
        <div className='pricing-description px-4'>
          <p className='text-center'>Unlimited users</p>
        </div>
      )}
      <div className='p-3'>
        <PaymentButton plan='Mogul' {...paymentButtonProps} />
      </div>
    </>
  );
};

const CustomButtons = ({ paymentButtonProps, planName }) => {
  const { activeTab, conditionForUpgrade } = paymentButtonProps || {};
  return (
    <>
      {activeTab === 'companies' && (
        <div className='pricing-description px-4'>
          <p className='text-center'>Unlimited users</p>
        </div>
      )}
      <div className='p-3'>
        <EnterprisePaymentButton planName={planName} conditionForUpgrade={conditionForUpgrade} />
      </div>
    </>
  );
};

const EnterprisePaymentButton = ({ planName, conditionForUpgrade }) => {
  const enterprisePlan = planName === 'Enterprise';
  return (
    <button
      className={`${
        enterprisePlan ? '' : ''
      } general-button rounded-pill payment-button px-3 py-2 payment-button-block pricing-contact-button`}
      disabled={enterprisePlan}
      style={{
        cursor: enterprisePlan ? 'auto' : 'pointer',
      }}
      onClick={() => {
        window.location.assign(getAppSite() === 'usa' ? '/' : `${returnCorrectWPUrl()}/demo`);
      }}
    >
      {enterprisePlan ? (
        <>
          {conditionForUpgrade ? (
            <>
              Upgrade <span className='pricing-padlock icon-padlock paragraph-p2 specific-left-margin-8 ' />
            </>
          ) : (
            'Your plan'
          )}
        </>
      ) : (
        'Book a demo'
      )}
    </button>
  );
};

export { FreeButtons, BasicButtons, ProButtons, MogulButtons, CustomButtons, EnterprisePaymentButton };
export default PaymentButton;
